import { Capacitor } from '@capacitor/core';

/**
 * App Store / Play subscription product IDs (same string where both stores use one ID).
 * Set before `npm run build` (baked into the bundle) and in CI before `cap sync`.
 *
 * App Store Connect (this app):
 *   REACT_APP_IAP_MONTHLY_PRODUCT_ID=Monthly
 *   REACT_APP_IAP_ANNUAL_PRODUCT_ID=Annual
 *
 * On native iOS, if those env vars are missing at build time, we default to
 * `Monthly` and `Annual` (case-sensitive; must match Connect product IDs).
 *
 * iOS StoreKit checklist: In Xcode → Signing & Capabilities → add **In-App Purchase**;
 * App Store Connect subscription product identifiers must match these strings exactly;
 * Paid Applications Agreement accepted; device signed into a **Sandbox** Apple ID for testing.
 * After `npx cap sync ios`, `scripts/fix-ios-plugin-class-names.js` (Podfile post_install) rewrites
 * `NativePurchasesPlugin` → `CapgoNativePurchases.NativePurchasesPlugin` in `ios/App/App/capacitor.config.json`
 * so the Swift pod registers with Capacitor (avoids UNIMPLEMENTED / "plugin not found").
 * Optional one-time / non-consumable (30-day unlimited AI tokens) — create in App Store Connect / Play Console
 * and set:
 *   REACT_APP_IAP_UNLIMITED_30D_PRODUCT_ID=unlimited
 * Purchase flow can be wired later; entitlements read `rbm_ai_unlimited_until_{uid}` when you set it after a successful buy.
 *
 * Google Play: base plan IDs from Play Console (subscription options), often aligned with the above:
 *   REACT_APP_IAP_ANDROID_MONTHLY_BASE_PLAN_ID=monthly
 *   REACT_APP_IAP_ANDROID_ANNUAL_BASE_PLAN_ID=annual
 *
 * On web, `requiresNativeSubscriptionGate()` is always false. On Android, both
 * product IDs must still be set via env (or extend defaults there if you align
 * Play Console IDs with iOS).
 */
const DEFAULT_IOS_SUBSCRIPTION_PRODUCT_IDS = {
  monthly: 'Monthly',
  annual: 'Annual',
};

export function getIapProductIds() {
  let monthly = (process.env.REACT_APP_IAP_MONTHLY_PRODUCT_ID || '').trim();
  let annual = (process.env.REACT_APP_IAP_ANNUAL_PRODUCT_ID || '').trim();
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    if (!monthly) monthly = DEFAULT_IOS_SUBSCRIPTION_PRODUCT_IDS.monthly;
    if (!annual) annual = DEFAULT_IOS_SUBSCRIPTION_PRODUCT_IDS.annual;
  }
  return { monthly, annual };
}

/** Non-subscription product (e.g. 30-day unlimited AI token cap). Optional until wired in UI. */
export function getIapUnlimited30dProductId() {
  return (process.env.REACT_APP_IAP_UNLIMITED_30D_PRODUCT_ID || '').trim();
}

export function getAndroidBasePlanIds() {
  return {
    monthly: (process.env.REACT_APP_IAP_ANDROID_MONTHLY_BASE_PLAN_ID || '').trim(),
    annual: (process.env.REACT_APP_IAP_ANDROID_ANNUAL_BASE_PLAN_ID || '').trim(),
  };
}

/** When false, the app does not block on native IAP (e.g. web or product IDs not set yet). */
export function requiresNativeSubscriptionGate() {
  if (!Capacitor.isNativePlatform()) return false;
  const { monthly, annual } = getIapProductIds();
  return Boolean(monthly && annual);
}

function getOrCreateIapAccountToken(uid) {
  if (!uid || typeof localStorage === 'undefined') return undefined;
  const key = `rbm_iap_acct_token_${uid}`;
  let v = localStorage.getItem(key);
  if (v && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(v)) {
    return v;
  }
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    v = crypto.randomUUID();
  } else {
    v = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v2 = c === 'x' ? r : (r & 0x3) | 0x8;
      return v2.toString(16);
    });
  }
  localStorage.setItem(key, v);
  return v;
}

async function loadPurchasesModule() {
  return import('@capgo/native-purchases');
}

function transactionIsEntitled(t, allowedIds) {
  if (!t || !allowedIds.includes(t.productIdentifier)) return false;
  if (t.revocationDate) return false;

  const platform = Capacitor.getPlatform();
  if (platform === 'ios') {
    if (t.subscriptionState === 'expired' || t.subscriptionState === 'revoked') return false;
    if (t.subscriptionState === 'subscribed' || t.subscriptionState === 'inGracePeriod' || t.subscriptionState === 'inBillingRetryPeriod') {
      return true;
    }
    if (t.isActive === true) return true;
    if (t.expirationDate) {
      return new Date(t.expirationDate) > new Date();
    }
    return false;
  }

  if (platform === 'android') {
    const stateOk = t.purchaseState === 'PURCHASED' || t.purchaseState === '1';
    const ack = t.isAcknowledged !== false;
    return stateOk && ack;
  }

  return false;
}

/**
 * @param {string} [firebaseUid] - Used for StoreKit appAccountToken on purchase; optional for read.
 */
export async function checkActiveProSubscription(firebaseUid) {
  if (!requiresNativeSubscriptionGate()) return true;

  const { NativePurchases, PURCHASE_TYPE } = await loadPurchasesModule();
  const { monthly, annual } = getIapProductIds();
  const allowed = [monthly, annual];

  try {
    const { isBillingSupported } = await NativePurchases.isBillingSupported();
    if (!isBillingSupported) return false;
  } catch {
    return false;
  }

  const opts = { productType: PURCHASE_TYPE.SUBS };
  if (Capacitor.getPlatform() === 'ios') {
    opts.onlyCurrentEntitlements = true;
  }

  try {
    const { purchases } = await NativePurchases.getPurchases(opts);
    return Array.isArray(purchases) && purchases.some((p) => transactionIsEntitled(p, allowed));
  } catch (e) {
    console.warn('getPurchases failed:', e?.message || e);
    return false;
  }
}

/**
 * Active Store subscription for Settings copy (plan + renewal / expiry when the store exposes it).
 * @returns {{ plan: 'monthly'|'annual'|null, expiresAt: Date|null }}
 */
export async function getNativeStoreSubscriptionDetails() {
  const empty = { plan: null, expiresAt: null };
  if (!requiresNativeSubscriptionGate()) return empty;
  try {
    const { NativePurchases, PURCHASE_TYPE } = await loadPurchasesModule();
    const { monthly, annual } = getIapProductIds();
    const allowed = [monthly, annual];
    const { isBillingSupported } = await NativePurchases.isBillingSupported();
    if (!isBillingSupported) return empty;

    const opts = { productType: PURCHASE_TYPE.SUBS };
    if (Capacitor.getPlatform() === 'ios') {
      opts.onlyCurrentEntitlements = true;
    }
    const { purchases } = await NativePurchases.getPurchases(opts);
    const list = Array.isArray(purchases) ? purchases.filter((p) => transactionIsEntitled(p, allowed)) : [];
    if (!list.length) return empty;

    const best = list.reduce((acc, p) => {
      const t = p?.expirationDate ? new Date(p.expirationDate).getTime() : 0;
      const accT = acc?.expirationDate ? new Date(acc.expirationDate).getTime() : 0;
      return t >= accT ? p : acc;
    }, list[0]);

    const id = String(best?.productIdentifier || '');
    const plan = id === annual ? 'annual' : id === monthly ? 'monthly' : null;
    const expRaw = best?.expirationDate;
    const expiresAt = expRaw ? new Date(expRaw) : null;
    if (expiresAt && Number.isNaN(expiresAt.getTime())) {
      return { plan, expiresAt: null };
    }
    return { plan, expiresAt };
  } catch (e) {
    console.warn('getNativeStoreSubscriptionDetails:', e?.message || e);
    return empty;
  }
}

export async function restoreNativePurchases() {
  const { NativePurchases } = await loadPurchasesModule();
  await NativePurchases.restorePurchases();
}

export async function openNativeSubscriptionManagement() {
  const { NativePurchases } = await loadPurchasesModule();
  await NativePurchases.manageSubscriptions();
}

/** @returns {{ monthlyProduct?: object, annualProduct?: object, products: object[] }} */
export async function fetchSubscriptionStoreProducts() {
  const { monthly, annual } = getIapProductIds();
  const { NativePurchases, PURCHASE_TYPE } = await loadPurchasesModule();
  let list = [];
  try {
    const { products } = await NativePurchases.getProducts({
      productIdentifiers: [monthly, annual],
      productType: PURCHASE_TYPE.SUBS,
    });
    list = Array.isArray(products) ? products : [];
  } catch (e) {
    console.warn('[IAP] getProducts failed:', e?.message || e);
    throw e;
  }

  const pickForMonthly = () => {
    if (Capacitor.getPlatform() === 'android') {
      return list.find((p) => p.planIdentifier === monthly)
        || list.find((p) => p.identifier === monthly);
    }
    return list.find((p) => p.identifier === monthly);
  };

  const pickForAnnual = () => {
    if (Capacitor.getPlatform() === 'android') {
      return list.find((p) => p.planIdentifier === annual)
        || list.find((p) => p.identifier === annual);
    }
    return list.find((p) => p.identifier === annual);
  };

  const monthlyProduct = pickForMonthly();
  const annualProduct = pickForAnnual();
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
    if (!monthlyProduct || !annualProduct) {
      const got = list.map((p) => p?.identifier || p?.planIdentifier).filter(Boolean);
      console.warn(
        `[IAP] Products missing for configured IDs (monthly="${monthly}", annual="${annual}"). `
          + `Store returned ${got.length ? got.join(', ') : 'no products — check App Store Connect IDs, Paid Apps agreement, and Sandbox test user'}.`,
      );
    }
  }

  return {
    monthlyProduct,
    annualProduct,
    products: list,
  };
}

/**
 * @param {'monthly'|'annual'} plan
 * @param {string} firebaseUid
 */
export async function purchaseNativeSubscription(plan, firebaseUid) {
  const { monthly, annual } = getIapProductIds();
  const productIdentifier = plan === 'annual' ? annual : monthly;
  const { NativePurchases, PURCHASE_TYPE } = await loadPurchasesModule();
  const appAccountToken = getOrCreateIapAccountToken(firebaseUid);

  const payload = {
    productIdentifier,
    productType: PURCHASE_TYPE.SUBS,
    appAccountToken,
  };

  if (Capacitor.getPlatform() === 'android') {
    const plans = getAndroidBasePlanIds();
    const basePlan = plan === 'annual' ? plans.annual : plans.monthly;
    payload.planIdentifier = basePlan || productIdentifier;
  }

  return NativePurchases.purchaseProduct(payload);
}
